import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import { useEnqueueStacks } from '@modules/layout/hooks';
import { useCurrentUser } from '@modules/user/hooks';
import { CreateCourseRequest } from '@modules/courses/requests';
import {
    GetCourseCollectionQuery,
    CreateCourseMutation,
    GetCoursesQuery,
} from '@modules/courses/graphql';
import { UserGrantActionToken, UserGrantCategoryToken } from '@modules/types/graphql';
import { CourseForm } from './course-form';

import type { CreateCourseFormValues } from '@modules/courses/requests';
import type {
    CreateCourseMutationType,
    CreateCourseMutationVariables,
} from '@modules/types/graphql';

type CourseCreateFormProps = {
    onCancel: () => void;
};

const CourseCreateForm = (props: CourseCreateFormProps): React.ReactElement => {
    const { onCancel } = props;

    const { t } = useTranslation(['course']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const { currentUser } = useCurrentUser();
    const { olCourseGrants } = currentUser.can({
        [UserGrantCategoryToken.olCourse]: [UserGrantActionToken.list],
    });

    const [createCourse] = useMutation<CreateCourseMutationType, CreateCourseMutationVariables>(
        CreateCourseMutation,
    );

    const initialValues = { name: '', sort: 0 };

    const handleSubmit = async (values: CreateCourseFormValues): Promise<void> => {
        try {
            let options: Parameters<typeof createCourse>[0] = {
                variables: {
                    item: new CreateCourseRequest(values),
                },
            };

            if (olCourseGrants.canList) {
                options = {
                    ...options,
                    refetchQueries: [
                        { query: GetCourseCollectionQuery },
                        { query: GetCoursesQuery },
                    ],
                    awaitRefetchQueries: true,
                };
            }

            const { data: createCourseData } = await createCourse(options);

            const result = createCourseData?.createOLCourse?.id;

            if (result) {
                enqueueSuccess(t('course:notifiers.add_course.success'));
                onCancel();
            } else {
                enqueueError(t('course:notifiers.add_course.error'));
            }
        } catch (e) {
            console.log(e);
            throw e;
        }
    };

    return (
        <CourseForm<CreateCourseFormValues>
            initialValues={initialValues}
            onSubmit={handleSubmit}
            onCancel={onCancel}
        />
    );
};

export { CourseCreateForm };
